import React from 'react';
import { Box } from '@material-ui/core';
import { Help, CheckCircle, Cancel } from '@material-ui/icons';

import { AvatarName, VolunteerProfile } from '../../../../../../common/AvatarName';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { Volunteer } from '../roster-types';
import {
  GetRosterScreenRosterData_vm_draftRoster_draftRosterings_volunteer_profile as DraftRosteringProfile,
  GetRosterScreenRosterData_vm_draftRoster_publishedRoster_rosterings_volunteer_profile as PublishedRosteringProfile,
} from '../__generated__/GetRosterScreenRosterData';
import { AvailabilityTrigger } from './AvailabilityTrigger';
import { useCampfireLazyQuery } from '../../../../../../global/network/useCampfireLazyQuery';
import { ROSTER_VOLUNTEER_INFO } from '../rosters-model.gql';
import {
  GetRosterVolunteerInfo,
  GetRosterVolunteerInfoVariables,
  GetRosterVolunteerInfo_vm_volunteer_activityRoles_tasks as ActivityRoleTasks,
} from '../__generated__/GetRosterVolunteerInfo';

const getName = (profile: any) => `${profile?.preferredName} ${profile?.lastName}`;

type RosterProfileType = DraftRosteringProfile | PublishedRosteringProfile;

interface ActivityRole {
  activityRoleId: string;
  icon: string;
  name: string;
  tasksCompleted: boolean;
}

type VolunteerCellProps = {
  roster: Volunteer;
  handleRoleBadgeClick?: (roleTasks: ActivityRoleTasks[], profile: RosterProfileType) => void;
  isRosterCompleted?: boolean;
  onChangeAvailability?: (volunteerId: string) => void;
  hideActions?: boolean;
  printOnly?: boolean;
  activityRoles?: ActivityRole[];
  taskBadges?: Array<{ taskId: string; title: string; icon: string; status: 'completed' | 'pending' | 'incomplete' }>;
};

const RosterStatusIndicator = ({ attending }: { attending: boolean | null }) =>
  attending === null ? (
    <Help fontSize='small' htmlColor='#00000024' style={{ fontSize: 15, background: 'white', borderRadius: '50%' }} />
  ) : attending ? (
    <CheckCircle
      fontSize='small'
      htmlColor='#7DD268'
      style={{ fontSize: 15, background: 'white', borderRadius: '50%' }}
    />
  ) : (
    <Cancel fontSize='small' color='error' style={{ fontSize: 15, background: 'white', borderRadius: '50%' }} />
  );

export const VolunteerCell = ({
  roster,
  handleRoleBadgeClick,
  isRosterCompleted,
  onChangeAvailability,
  hideActions,
  printOnly,
  activityRoles: batchedActivityRoles,
  taskBadges,
}: VolunteerCellProps) => {
  const { theme } = useCampfireTheme();

  const [fetchVolunteerInfo, lazyResult] = useCampfireLazyQuery<
    GetRosterVolunteerInfo,
    GetRosterVolunteerInfoVariables
  >(ROSTER_VOLUNTEER_INFO);

  const [pendingActivityRoleId, setPendingActivityRoleId] = React.useState<string | null>(null);

  const activityRoles = batchedActivityRoles || [];

  const activityRolesParsed = React.useMemo(() => {
    return activityRoles.map((role) => ({
      ...role,
      isCompletedTask: role.tasksCompleted,
    }));
  }, [activityRoles]);

  React.useEffect(() => {
    if (lazyResult.data?.vm.volunteer?.activityRoles && handleRoleBadgeClick && pendingActivityRoleId) {
      const roleTasks =
        lazyResult.data.vm.volunteer.activityRoles.find((item) => item.activityRoleId === pendingActivityRoleId)
          ?.tasks || [];
      handleRoleBadgeClick(roleTasks, roster.profile);
      setPendingActivityRoleId(null);
    }
  }, [lazyResult.data, handleRoleBadgeClick, roster.profile, pendingActivityRoleId]);

  const onRoleBadgeClick = (activityRoleId: string) => {
    if (handleRoleBadgeClick) {
      setPendingActivityRoleId(activityRoleId);
      fetchVolunteerInfo({
        variables: {
          volunteerId: roster.volunteerId,
          profileId: roster.profile.profileId,
        },
      });
    }
  };

  return (
    <Box
      display='flex'
      alignItems='center'
      width='100%'
      justifyContent={printOnly ? 'center' : 'space-between'}
      height='100%'
    >
      {printOnly ? (
        <AvatarName
          avatarUrl={roster.profile.avatarUrl}
          name={`${getName(roster?.profile)}`}
          width='200px'
          avatarColor={!roster.enrolled ? theme.color.rosters.rostered : undefined}
          statusIndicator={<RosterStatusIndicator attending={roster.attending} />}
          activityRoles={activityRolesParsed}
          taskBadges={taskBadges}
        />
      ) : (
        <>
          <VolunteerProfile
            profileId={roster.profile.profileId}
            userId={roster.profile.userId}
            lastName={roster.profile.lastName}
            preferredName={roster.profile.preferredName}
            name={`${getName(roster?.profile)}`}
            avatarUrl={roster.profile.avatarUrl}
            avatarColor={!roster.enrolled ? theme.color.rosters.rostered : undefined}
            activityRoles={activityRolesParsed}
            taskBadges={taskBadges}
            noTrim
            tooltipAll={false}
            statusIndicator={<RosterStatusIndicator attending={roster.attending} />}
            onRoleBadgeClick={onRoleBadgeClick}
          />
          {roster.enrolled && !hideActions && !isRosterCompleted && roster.volunteerId && (
            <AvailabilityTrigger
              onClick={() => {
                if (onChangeAvailability) onChangeAvailability(roster.volunteerId);
              }}
              unavailable={!roster.unavailable}
              volunteerId={roster.volunteerId}
            />
          )}
        </>
      )}
    </Box>
  );
};
